/**
 * What Cold War said about a position it sized (MDOT-25671): the value the user typed came back
 * different because of the trading rules Cold War applied — a lot, a minimum denomination and its
 * increment, a minimum initial subscription — which its answer names (`../position-sizing/answer`).
 *
 * The row keeps the message until its next answer. As it lands, the row's quantity pulses blue for
 * {@link SIZING_MESSAGE_PULSE_MS}. The builder toasts the message of a typed edit, and the resized-rows
 * banner lists the others. Nothing here calls the API or touches the builder.
 */
import type { Currencies } from "@eu.mdotm.api/sphere";
import { unreachable } from "@mdotm/utils/runtime";
import type { TFunction } from "i18next";
import { formatNumber } from "$root/localization/formatters";
import { formatCurrencyAmount } from "$root/utils/currency";
import { labMul, MAX_PERCENT_DECIMAL_PLACES } from "$root/utils/monetary-math";
import type { Instrument } from "../../const";
import type { QuantityEditResult } from "../derive";
import type { SizingAnswer, TradingRule } from "../position-sizing/answer";
import type { SizingEdit, SizingField } from "../position-sizing/sizing";

/** `requested` and `applied` are read as the cell `field` reads them, so a weight is a percentage. */
export type SizingMessage = {
	field: SizingField;
	requested: number;
	applied: number;
	rules: TradingRule[];
	currency: Currencies | undefined;
	/** The row is held by value, so the rules adjusted its market value: it has no quantity. */
	heldByValue: boolean;
	receivedAt: string;
	/** The builder toasted it as it landed, so the resized-rows banner leaves it out. */
	toasted: boolean;
};

/** How long the quantity of a row pulses once its message lands: the length of the pulse animation. */
export const SIZING_MESSAGE_PULSE_MS = 3200;

/**
 * What a sizing message adds to a row. One named block for the same reason as the other row states
 * in `../../const`: dropping the feature drops this type, and the compiler names every reader.
 */
export type InstrumentSizingMessageAttributes = {
	sizingMessage?: SizingMessage;
};

type RuledAnswer = Extract<SizingAnswer, { status: "OK" | "NOT_SHAREABLE" }>;

function appliedValue(answer: RuledAnswer, field: SizingField): number | undefined {
	switch (field) {
		case "QUANTITY":
			return answer.status === "OK" ? answer.shares : undefined;
		case "MARKET_VALUE":
			return answer.value;
		case "WEIGHT":
			return answer.weight == null ? undefined : labMul(answer.weight, 100);
		default:
			return unreachable(field);
	}
}

/**
 * The message about `edit`, landed at `receivedAt`; `undefined` when Cold War applied no rule, or the
 * value it applied reads the same as the typed one.
 */
export function sizingMessageOf(
	answer: SizingAnswer,
	edit: SizingEdit,
	context: { currency: Currencies | undefined; heldByValue: boolean; receivedAt: string; toasted: boolean },
): SizingMessage | undefined {
	if ((answer.status !== "OK" && answer.status !== "NOT_SHAREABLE") || answer.rules.length === 0) {
		return undefined;
	}
	const applied = appliedValue(answer, edit.field);
	if (
		applied == null ||
		formatValue(edit.field, applied, context.currency) === formatValue(edit.field, edit.value, context.currency)
	) {
		return undefined;
	}
	return { field: edit.field, requested: edit.value, applied, rules: answer.rules, ...context };
}

/** Whether the row's quantity is still pulsing at `now` (epoch ms) for its message. */
export function isPulsing(sizingMessage: SizingMessage | undefined, now: number): boolean {
	return sizingMessage != null && now - Date.parse(sizingMessage.receivedAt) < SIZING_MESSAGE_PULSE_MS;
}

/** Whether the rules took the position to zero. */
export function isClearedToZero({ applied }: SizingMessage): boolean {
	return applied === 0;
}

/** How the row is named in a message. */
export function instrumentLabel(row: Instrument): string {
	return row.name ?? row.alias ?? row.tickerName ?? row.id;
}

/** Whole shares print without decimals; a fractional quantity keeps every decimal. */
function formatQuantity(quantity: number): string {
	return formatNumber(quantity, { minDecimalPlaces: 0, maxDecimalPlaces: MAX_PERCENT_DECIMAL_PLACES });
}

function formatValue(field: SizingField, value: number, currency: Currencies | undefined): string {
	switch (field) {
		case "QUANTITY":
			return formatQuantity(value);
		case "MARKET_VALUE":
			return formatCurrencyAmount(value, currency);
		case "WEIGHT":
			return `${formatNumber(value, { minDecimalPlaces: 2, maxDecimalPlaces: MAX_PERCENT_DECIMAL_PLACES })}%`;
		default:
			return unreachable(field);
	}
}

/** One clause per rule, in the order Cold War gave them. A bond's minimum denomination speaks for its increment. */
function reasons(t: TFunction, rules: TradingRule[], currency: Currencies | undefined): string[] {
	const hasMinimum = rules.some((rule) => rule.rule === "DENOMINATION_MIN");
	const told = hasMinimum ? rules.filter((rule) => rule.rule !== "DENOMINATION_INCREMENT") : rules;
	return told.map((rule) => {
		switch (rule.rule) {
			case "LOT_SIZE":
				return t("EDIT_COMPOSITION.SIZING_MESSAGE.RULE.LOT_SIZE", { lot: formatQuantity(rule.lot) });
			case "DENOMINATION_MIN":
				return t("EDIT_COMPOSITION.SIZING_MESSAGE.RULE.DENOMINATION_MIN", { minimum: formatQuantity(rule.minimum) });
			case "DENOMINATION_INCREMENT":
				return t("EDIT_COMPOSITION.SIZING_MESSAGE.RULE.DENOMINATION_INCREMENT", {
					increment: formatQuantity(rule.increment),
				});
			case "MIN_INITIAL_SUBSCRIPTION":
				return t("EDIT_COMPOSITION.SIZING_MESSAGE.RULE.MIN_INITIAL_SUBSCRIPTION", {
					amount: formatCurrencyAmount(rule.amount, currency, Number.isInteger(rule.amount) ? 0 : undefined),
				});
			case "UNKNOWN":
				return t("EDIT_COMPOSITION.SIZING_MESSAGE.RULE.UNKNOWN", {
					name: rule.name,
					value: formatQuantity(rule.value),
				});
			default:
				return unreachable(rule);
		}
	});
}

/** The message as the user reads it: one sentence, the rules joined in the user's language. */
export function describeSizingMessage(
	t: TFunction,
	instrument: string,
	{ rules, currency, heldByValue }: SizingMessage,
): string {
	const sentence = heldByValue
		? "EDIT_COMPOSITION.SIZING_MESSAGE.SENTENCE_BY_VALUE"
		: "EDIT_COMPOSITION.SIZING_MESSAGE.SENTENCE";
	return t(sentence, { instrument, reasons: reasons(t, rules, currency) });
}

/** `result` with row `id` carrying `sizingMessage`, or carrying none when Cold War said nothing this time. */
export function withSizingMessage(
	result: QuantityEditResult,
	id: string,
	sizingMessage: SizingMessage | undefined,
): QuantityEditResult {
	return { ...result, rows: result.rows.map((row) => (row.id === id ? { ...row, sizingMessage } : row)) };
}
